/**
 * EtagConflictDialog - Dialog for 409 ETag conflict resolution
 *
 * Displays when a save operation fails due to concurrent modification
 * (HTTP 409 Conflict with ETag mismatch), providing options to:
 * - Reload: Fetch the latest configuration (discarding local changes)
 * - Overwrite: Force save local changes over the server version
 * - Cancel: Return to editing without any action
 */

import React from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

/**
 * Props for the EtagConflictDialog component
 */
export interface EtagConflictDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback when user chooses to reload the latest configuration */
  onReload: () => void;
  /** Callback when user chooses to overwrite with their changes */
  onOverwrite: () => void;
  /** Callback when user cancels and returns to editing */
  onCancel: () => void;
}

/**
 * EtagConflictDialog Component
 *
 * A confirmation dialog that appears when saving fails due to an ETag
 * mismatch (another user modified the configuration). Provides three options:
 * - Reload: Discard local changes and fetch the latest server version
 * - Overwrite: Force save local changes, overwriting the server version
 * - Cancel: Return to editing without taking action
 */
export const EtagConflictDialog: React.FC<EtagConflictDialogProps> = ({
  isOpen,
  onReload,
  onOverwrite,
  onCancel,
}) => {
  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Configuration Changed</AlertDialogTitle>
          <AlertDialogDescription>
            Another user has modified this connector's configuration since you
            loaded it. You can reload to see their changes (discarding your
            changes), or overwrite their changes with yours.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={onOverwrite}>
            Overwrite
          </Button>
          <Button onClick={onReload}>Reload</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
